import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { MessageService } from '../../../../../../message/message.service';
import { SsoAccountFacade } from '../../../../../sso-account/domain/+state/sso-account.facade';
import { IdentityVerificationFacade } from '../../../../domain/+state/identity-verification.facade';
import { IdentityVerificationStatus, IdVerseFailedAction, IdVerseStateData } from '../../../../domain/models/identity-verification.model';
import { DeviceDetectionService } from '../../../../../../../lib/device/device-detection.service';
import { DeviceData, DeviceType } from '../../../../../../../lib/device/device-detection.model';
import { IdVerseFlowType, IdVerseContentConfig } from '../models/id-verse-content.model';
import { IdVerseDesktopFlowService } from './id-verse-desktop-flow.service';
import { IdVerseMobileFlowService } from './id-verse-mobile-flow.service';
import { IdVerseContentStateService } from './id-verse-content-state.service';

@Injectable()
export class IdVerseFlowHandlerService {
  constructor(
    private readonly messageService: MessageService,
    private readonly ssoAccountFacade: SsoAccountFacade,
    private readonly identityVerificationFacade: IdentityVerificationFacade,
    private readonly deviceDetectionService: DeviceDetectionService,
    private readonly desktopFlowService: IdVerseDesktopFlowService,
    private readonly mobileFlowService: IdVerseMobileFlowService,
    private readonly stateService: IdVerseContentStateService,
  ) { }

  loadInitialData(config: IdVerseContentConfig): void {
    combineLatest([
      this.ssoAccountFacade.getCurrentSsoAccount$(),
      this.identityVerificationFacade.getIdVerseState$(),
      this.deviceDetectionService.getDeviceData$(),
    ]).pipe(take(1)).subscribe(([ssoAccount, stateData, deviceData]) => {
      if (!ssoAccount || !stateData) {
        this.messageService.errorDefault('Unable to get identity verification data.');
        return;
      }

      this.stateService.update({ verificationUrl: stateData.verificationUrl });

      switch (stateData.status) {
        case IdentityVerificationStatus.None:
          this.handleNoneStatus(ssoAccount.id, deviceData, config);
          break;

        case IdentityVerificationStatus.Pending:
          this.handleInitialPending(deviceData, config);
          break;

        case IdentityVerificationStatus.Syncing:
          this.handleSyncingStatus(deviceData);
          break;

        case IdentityVerificationStatus.Success:
          this.handleSuccessStatus();
          break;

        case IdentityVerificationStatus.Failed:
          this.handleFailedStatus(stateData.onFailedAction);
          break;

        default:
          this.showUnknownStatusError();
          break;
      }
    });
  }

  reloadInitialData(config: IdVerseContentConfig): void {
    this.stopPolling();
    this.identityVerificationFacade.clearState();
    this.loadInitialData(config);
  }

  handlePendingContinue(customerId: number, verificationUrl: string, deviceData: DeviceData, config: IdVerseContentConfig): void {
    if (deviceData?.type !== DeviceType.Desktop) {
      this.mobileFlowService.handlePendingContinue(verificationUrl);
      return;
    }

    this.identityVerificationFacade.refreshIdVerseState()
      .subscribe((stateData: IdVerseStateData) => this.desktopFlowService.handlePendingContinue(stateData, customerId, config));
  }

  onTryAgain(customerId: number, deviceData: DeviceData): void {
    this.storeNewVerification(customerId).subscribe(verificationUrl => {
      if (deviceData?.type !== DeviceType.Desktop) {
        this.mobileFlowService.handleTryAgain(verificationUrl);
        return;
      }

      this.desktopFlowService.handleTryAgain();
    });
  }

  onContinueInBrowser(): void {
    this.updateFlowType(IdVerseFlowType.StandardPendingFlow);
  }

  stopPolling(): void {
    this.desktopFlowService.stopPolling();
    this.mobileFlowService.stopPolling();
  }

  private showUnknownStatusError(): void {
    this.messageService.errorDefault('Unknown identity verification status.');
  }

  private handleNoneStatus(customerId: number, deviceData: DeviceData, config: IdVerseContentConfig): void {
    this.storeNewVerification(customerId).subscribe(() => {
      this.handleInitialPending(deviceData, config);
    });
  }

  private storeNewVerification(customerId: number): Observable<string> {
    return this.identityVerificationFacade.storeVerification(customerId)
      .pipe(
        filter(response => {
          if (!response.verificationUrl) {
            this.messageService.errorDefault('Unable to start new verification. Please try again later.');
            return false;
          }
          return true;
        }),
        tap(response => this.stateService.update({ verificationUrl: response.verificationUrl })),
        map(response => response.verificationUrl)
      );
  }

  private handleSyncingStatus(deviceData: DeviceData): void {
    if (deviceData?.type === DeviceType.Desktop) {
      this.desktopFlowService.handleSyncingStatus();
      return;
    }

    this.mobileFlowService.handleSyncingStatus();
  }

  private handleInitialPending(
    deviceData: DeviceData,
    config: IdVerseContentConfig,
  ): void {
    if (deviceData?.type === DeviceType.Desktop) {
      this.desktopFlowService.handlePendingInitial(config);
      return;
    }

    this.mobileFlowService.handlePendingInitial(config)
      .pipe(take(1))
      .subscribe((shouldReload: boolean) => {
        if (shouldReload) {
          this.reloadInitialData(config);
        }
      });
  }

  private handleSuccessStatus(): void {
    this.stopPolling();
    this.updateFlowType(IdVerseFlowType.Success);
  }

  private handleFailedStatus(onFailedAction: IdVerseFailedAction): void {
    this.stopPolling();
    this.updateFlowType(IdVerseFlowType.Failed, true, onFailedAction);
  }

  private updateFlowType(flowType: IdVerseFlowType, skipMobileAppPromo: boolean = true, failedAction?: IdVerseFailedAction): void {
    this.stateService.update({ currentFlowType: flowType, skipMobileAppPromo, failedAction: failedAction || null });
  }
}
